import { yupResolver } from "@hookform/resolvers/yup";
import { RouteProp, useRoute } from "@react-navigation/native";
import { CardStyleInterpolators } from "@react-navigation/stack";
import {
  LayoutNodeFilterType,
  LayoutNodeInput,
  LayoutNodeType,
} from "@towersystems/roam-common/lib/generated-types";
import { FormProvider, useForm } from "react-hook-form";
import { CategoryListScreen } from "../../../../components/CategoryListScreen";
import { DepartmentListScreen } from "../../../../components/DepartmentListScreen";
import { ModalCloseButton } from "@towersystems/roam-common-native";
import { EditFilterQuickKeyRootParamList } from "../../../../types";
import { useEditTile } from "../../../../utilities/layout";
import {
  defaultLayoutNodeInputValues,
  filterNodeInputSchema,
} from "../../../../utilities/layout/constants";
import { createStackHeaderNavigator } from "@towersystems/roam-common-native";
import { normalizeSelectedValues } from "../../../../utilities/transforms";
import { FilterQuickKeyFormProvider } from "../../components/LayoutNodeFilterForm/components";
import { EditFilterQuickKeyScreen } from "../EditFilterQuickKeyScreen";
import { quickKeyColors } from "../../../../utilities/quick-keys";
import { GenerateAiIconRootScreen } from "../GenerateAiIconScreens";
import { GenerateIconScreenContext } from "../GenerateAiIconScreens/common";
import { ColorPickerScreen } from "../../components/ColorPickerScreen/ColorPickerScreen";

export function EditFilterQuickKeyRootScreen() {
  const route =
    useRoute<
      RouteProp<EditFilterQuickKeyRootParamList, "EditFilterQuickKeyScreen">
    >();
  const { item } = useEditTile();
  const Stack = createStackHeaderNavigator<EditFilterQuickKeyRootParamList>();

  const filterType = route.params?.filter || LayoutNodeFilterType.CATEGORY;

  const methods = useForm<LayoutNodeInput>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: normalizeSelectedValues(makeDefaultValues(), item?.config),
    resolver: yupResolver(filterNodeInputSchema),
  });
  const { setValue } = methods;

  function makeDefaultValues() {
    return {
      ...defaultLayoutNodeInputValues,
      color: quickKeyColors.action.background,
      type: LayoutNodeType.FILTER,
      filter: {
        type: filterType,
        values: [],
      },
    };
  }

  return (
    <GenerateIconScreenContext.Provider
      value={{
        onSubmit: async (base64Image: string) => {
          const time = new Date().getTime();
          const id = `${time}-${Math.floor(Math.random() * 1000)}`;

          // Pass the generated icon back to the previous screen
          const name = `key-icon-${id}.png`;
          setValue(
            "base64Image",
            {
              base64: base64Image,
              imageName: name,
            },
            {
              shouldDirty: true,
              shouldValidate: true,
            }
          );
        },
      }}
    >
      <FormProvider {...methods}>
        <FilterQuickKeyFormProvider>
          <Stack.Navigator>
            <Stack.Screen
              name="EditFilterQuickKeyScreen"
              component={EditFilterQuickKeyScreen}
              initialParams={{ filter: filterType }}
              options={{
                headerShown: true,
                headerLeft: () => <ModalCloseButton type="Close" />,
                title: "",
              }}
            />
            <Stack.Group
              screenOptions={{
                animationEnabled: true,
                headerShown: true,
                headerLeft: () => <ModalCloseButton type="Back" />,
                cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
              }}
            >
              <Stack.Screen
                name="CategoryListScreen"
                component={CategoryListScreen}
                options={{ title: "Search Category List" }}
              />
              <Stack.Screen
                name="DepartmentListScreen"
                component={DepartmentListScreen}
                options={{ title: "Search Department List" }}
              />
              <Stack.Screen
                name="GenerateAiIconRootScreen"
                component={GenerateAiIconRootScreen}
                options={{
                  title: "",
                  headerShown: false,
                }}
              />
              <Stack.Screen
                name="ColorPickerScreen"
                component={ColorPickerScreen}
                options={{
                  title: "",
                }}
              />
            </Stack.Group>
          </Stack.Navigator>
        </FilterQuickKeyFormProvider>
      </FormProvider>
    </GenerateIconScreenContext.Provider>
  );
}
